import { useCallback, useEffect, useState } from "react";
import { BoundingBox, CanvasObject, ResizeHandle } from "../types";
import { applyDrag } from "../utils/bboxDrag";

interface DragState {
	objectId: string;
	startPos: { x: number; y: number };
	startBbox: BoundingBox;
	handle: ResizeHandle;
	canvasElement: Element;
}

export const useBboxManipulation = (
	objects: CanvasObject[],
	canvasSize: { width: number; height: number },
	onUpdate: (objectId: string, bbox: BoundingBox) => void,
) => {
	const [selectedObjectId, setSelectedObjectId] = useState<string | null>(null);
	const [dragState, setDragState] = useState<DragState | null>(null);

	const handleMouseDown = useCallback(
		(e: React.MouseEvent, objectId: string, handle: ResizeHandle = null) => {
			e.stopPropagation();
			const object = objects.find((o) => o.id === objectId);
			if (!object) return;

			setSelectedObjectId(objectId);

			const canvasElement = (e.currentTarget as HTMLElement).closest("[data-canvas-container]");
			if (canvasElement) {
				const rect = canvasElement.getBoundingClientRect();
				setDragState({
					objectId,
					startPos: { x: e.clientX - rect.left, y: e.clientY - rect.top },
					startBbox: { ...object.bbox },
					handle,
					canvasElement,
				});
			}
		},
		[objects],
	);

	useEffect(() => {
		if (!dragState) return;

		const { objectId, startPos, startBbox, handle, canvasElement } = dragState;

		const handleMouseMove = (e: MouseEvent) => {
			if (!canvasElement) return;

			const rect = canvasElement.getBoundingClientRect();
			const dx = ((e.clientX - rect.left - startPos.x) / canvasSize.width) * 100;
			const dy = ((e.clientY - rect.top - startPos.y) / canvasSize.height) * 100;

			onUpdate(objectId, applyDrag(startBbox, dx, dy, handle));
		};

		const handleMouseUp = () => setDragState(null);

		window.addEventListener("mousemove", handleMouseMove);
		window.addEventListener("mouseup", handleMouseUp);
		return () => {
			window.removeEventListener("mousemove", handleMouseMove);
			window.removeEventListener("mouseup", handleMouseUp);
		};
	}, [dragState, canvasSize, onUpdate]);

	return {
		selectedObjectId,
		setSelectedObjectId,
		handleMouseDown,
	};
};
